// MyRequestsModal - CHG-0100 (Connor 2026-10-23). Opened from the account menu.
// Mine (default): every Retail request in the person's name from the Innovation Hub change tracker (life-ring
// reports and items filed for them). All: every Retail request, with who asked. Never loans or other platforms: the
// server reads ROP items only (GET /v3/rop/my-requests). Status in plain words; no compliance details.
// Mine / All uses the app's one scope switch (ScopeSwitch, the Home panels' toggle), shown only when the server says
// canSeeAll (Bill 2026-10-26, ask #285: every pilot user during the pilot; afterwards the roles in
// support.myRequests.allViewerRoles). Mine is the default and is remembered for the session like the Home panels.
// The server refuses scope=all for anyone else.
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Banner, ModalShell, Pill } from '@/components/primitives';
import type { PillVariant } from '@/components/primitives/Pill';
import { useApiClient } from '@/contexts/ApiClientContext';
import { ScopeSwitch, useScopeMode } from '@/modules/Home/widgets/teamScope';
import type { ScopeViews } from '@/modules/Home/widgets/teamScope';
import { ApiError } from '@retail-ops/api-client';
import '@/modules/Home/styles/home.css';

interface MyRequestItem {
  key: string;
  summary: string;
  statusLabel: string;
  statusTone: 'neutral' | 'info' | 'good' | 'warn';
  requestedByName: string;
  mine: boolean;
  requestedAt: string;
  fixedAt?: string | null;
  updatedAt?: string | null;
}

interface MyRequestsResponse {
  success: boolean;
  scope: 'mine' | 'all';
  canSeeAll?: boolean;
  available: boolean;
  items: MyRequestItem[];
  error?: string;
}

const TONE: Record<MyRequestItem['statusTone'], PillVariant> = { neutral: 'muted', info: 'info', good: 'ok', warn: 'warn' };

function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const rowStyle: CSSProperties = { padding: '10px 0', borderBottom: '1px solid var(--border, #e3e7ee)' };
const metaStyle: CSSProperties = { fontSize: 12, color: 'var(--text-muted, #5b6573)', marginTop: 4 };
// The status stays on one line; the summary wraps beside it (a long label wrapped into a ragged blob).
const pillCellStyle: CSSProperties = { flexShrink: 0, whiteSpace: 'nowrap' };

export interface MyRequestsModalProps {
  open: boolean;
  onClose: () => void;
}

export function MyRequestsModal({ open, onClose }: MyRequestsModalProps): JSX.Element | null {
  const client = useApiClient();
  const [canSeeAll, setCanSeeAll] = useState(false);
  const views: ScopeViews = { team: false, all: canSeeAll, canReassign: false, teamBasis: '' };
  const [mode, setScope] = useScopeMode('rop.myRequests.scope', views);
  const scope = mode === 'all' ? 'all' : 'mine';
  const [data, setData] = useState<MyRequestsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const r = await client.http.get<MyRequestsResponse>(`/v3/rop/my-requests?scope=${scope}`);
        if (!cancelled) { setData(r); setCanSeeAll(!!r.canSeeAll); }
      } catch (e: unknown) {
        // All taken away since the switch was shown: fall back to their own list.
        if (!cancelled && scope === 'all' && ApiError.isApiError(e) && e.httpStatus === 403) { setCanSeeAll(false); setScope('mine'); return; }
        if (!cancelled) setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, scope, client]);

  if (!open) return null;

  const items = data?.items ?? [];
  const unavailable = data != null && !data.available;

  return (
    <ModalShell open={open} onClose={onClose} title="My requests" size="md">
      <div className="my-requests">
        <ScopeSwitch mode={scope} views={views} onChange={setScope} label="requests" />
      </div>

      {err && <Banner tone="err" title="Couldn't load your requests">{err}</Banner>}
      {unavailable && <Banner tone="info">{data?.error ?? "Your requests can't be shown right now."}</Banner>}
      {loading && !data && <div style={metaStyle}>Loading…</div>}

      {!err && !unavailable && data && items.length === 0 && (
        <div style={metaStyle}>
          {scope === 'mine'
            ? 'Nothing here yet. Reports you send with the life ring, and requests made for you, show up here.'
            : 'No Retail requests yet.'}
        </div>
      )}

      {!unavailable && items.length > 0 && (
        <div aria-live="polite">
          {items.map((it) => (
            <div key={it.key} style={rowStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 14 }}>{it.summary}</div>
                <span style={pillCellStyle}><Pill variant={TONE[it.statusTone] ?? 'muted'}>{it.statusLabel}</Pill></span>
              </div>
              <div style={metaStyle}>
                {it.key} · Asked {shortDate(it.requestedAt)}
                {scope === 'all' && ` by ${it.mine ? 'you' : it.requestedByName}`}
                {it.fixedAt ? ` · Live ${shortDate(it.fixedAt)}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}
